/**
 * Turns a claims band into a seamless horizontal marquee.
 *
 * The blocks are rendered once by Liquid, so the loop is built here: the row is
 * cloned until it is at least twice the width of its viewport, then the track
 * is animated by exactly half its width. At that point the second half sits
 * where the first half started, so the restart is invisible.
 */
class ClaimsMarquee extends HTMLElement {
  connectedCallback() {
    // The wrapper is always in the markup; it only animates when switched on.
    if (this.dataset.marquee !== 'true') return;

    this.list = this.querySelector('.claims-band__list');
    if (!this.list) return;

    // Re-runs on resize because the number of clones depends on the width.
    this.build();
    this.resizeObserver = new ResizeObserver(() => this.rebuild());
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  rebuild() {
    const width = this.offsetWidth;
    if (width === this.lastWidth) return;
    this.lastWidth = width;
    this.build();
  }

  build() {
    const list = this.list;

    // Start from the original blocks only, so repeated builds do not stack.
    this.originals ??= Array.from(list.children).map((node) => node.cloneNode(true));
    list.replaceChildren(...this.originals.map((node) => node.cloneNode(true)));

    const groupWidth = list.scrollWidth;
    if (!groupWidth) return;

    // Enough copies to cover twice the viewport, and always an even count so
    // translating half the track lands exactly on a copy boundary.
    const needed = Math.max(2, Math.ceil((this.offsetWidth * 2) / groupWidth));
    const copies = needed % 2 === 0 ? needed : needed + 1;

    for (let i = 1; i < copies; i++) {
      for (const node of this.originals) {
        const clone = node.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.dataset.marqueeClone = 'true';
        list.appendChild(clone);
      }
    }

    // Speed is expressed as pixels per second so long and short bands drift at
    // the same visual pace instead of one racing the other.
    const pxPerSecond = Number(this.dataset.speed) || 40;
    const distance = list.scrollWidth / 2;
    list.style.setProperty('--claims-marquee-duration', `${distance / pxPerSecond}s`);
  }
}

if (!customElements.get('claims-marquee')) {
  customElements.define('claims-marquee', ClaimsMarquee);
}
